import { GridColumn as Column } from '@progress/kendo-react-grid';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { DateTimeCell } from 'src/components/shared/DataGrid/DateCell';
import { DropdownCell } from 'src/components/shared/DataGrid/EditableGrid/DropdownCell';
import { EditableGrid } from 'src/components/shared/DataGrid/EditableGrid/EditableGrid';
import {
  MixtureTypeDetailDto,
  MixtureTypeParameterDto
} from 'src/store/mixturetypes/mixturetype';
import { selectActiveParameters } from 'src/store/parameters/parameters.selectors';
import { SpecificationParameterDto } from 'src/store/specifications/specification';
import { useAuth } from '../../Auth/AuthContext';

interface Props {
  mixtureType: Nullable<MixtureTypeDetailDto>;
  onParametersChanged: (parameters: MixtureTypeParameterDto[]) => void;
}

let uniqueId = 0;

export const StandardPractice: React.FC<Props> = ({
  mixtureType,
  onParametersChanged
}) => {
  const { t } = useTranslation();

  const [parameters, setParameters] = useState<MixtureTypeParameterDto[]>([]);
  const [editedParameters, setEditedParameters] = useState<
    MixtureTypeParameterDto[]
  >([]);

  const { user } = useAuth();

  const parameters$ = useSelector(selectActiveParameters);

  useEffect(() => {
    if (mixtureType) {
      setParameters(mixtureType.mixtureTypeParameters);
      setEditedParameters(mixtureType.mixtureTypeParameters);
    }
  }, [mixtureType]);

  useEffect(() => {
    onParametersChanged(editedParameters);
  }, [editedParameters, onParametersChanged]);

  const onCancel = () => {
    setParameters([...(mixtureType?.mixtureTypeParameters ?? [])]);
  };

  const onAddParameter = () => {
    const parameter: MixtureTypeParameterDto = {
      id: --uniqueId,
      column: null,
      deviation: null,
      mixDate: new Date(),
      mixtureTypeId: mixtureType?.id,
      userId: user?.id,
      mixtureTypeName: '',
      parameterName: '',
      userInitials: ''
    };
    setParameters([parameter, ...editedParameters]);
  };

  const onDeleteParameter = (parameter: SpecificationParameterDto) => {
    setParameters(parameters.filter(p => p.id !== parameter.id));
    setEditedParameters(editedParameters.filter(p => p.id !== parameter.id));
  };

  return (
    <Fragment>
      <div className="flex mb-24">
        <SecondaryButton
          icon="la-plus"
          onClick={onAddParameter}
          className="mr-24"
        >
          {t('add.verb')}
        </SecondaryButton>
        <TertiaryButton onClick={onCancel}>{t('undo')}</TertiaryButton>
      </div>
      <EditableGrid
        items={parameters}
        onItemsChange={items => setEditedParameters(items)}
      >
        <Column
          field="column"
          title={t('props.column')}
          width="180px"
          editor="numeric"
        />
        <Column
          field="parameterId"
          title={t('parameter.singular')}
          width="220px"
          cell={props => (
            <DropdownCell
              {...props}
              items={parameters$}
              uniqueField="id"
              optionField="name"
            />
          )}
        />
        <Column
          field="deviation"
          title={t('props.deviation')}
          width="180px"
          editor="numeric"
        />
        <Column
          field="mixDate"
          title={t('props.date')}
          editable={false}
          width="240px"
          cell={props => <DateTimeCell {...props} />}
        />
        <Column
          field="userInitials"
          title={t('user.singular')}
          editable={false}
          width="220px"
        />
        <Column
          width="60px"
          filterable={false}
          editable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDeleteParameter(props.dataItem)} />
            </td>
          )}
        />
      </EditableGrid>
    </Fragment>
  );
};
